"use client";

import { useId, useState, type ReactNode } from "react";

/**
 * Wspólne klocki zakładek Ustawień. Zasada czytelności: nazwa opcji i
 * kontrolka w jednym wierszu, wyjaśnienie pod „?" (kafelek podpowiedzi —
 * hover na komputerze, dotknięcie na telefonie). Pod opcją zostają tylko
 * ostrzeżenia i statusy, które trzeba widzieć od razu.
 */

/** Przycisk „?" z wyjaśnieniem opcji (obsługa w `Podpowiedzi.tsx`). */
export function Pomoc({ tekst }: { tekst: string }) {
  return (
    <button type="button" className="pomoc" data-podpowiedz={tekst} aria-label="Wyjaśnienie">
      ?
    </button>
  );
}

/** Wiersz opcji: nazwa (+ „?") z lewej, kontrolka z prawej; na telefonie kontrolka pod nazwą. */
export function Wiersz({
  etykieta,
  pomoc,
  htmlFor,
  children,
}: {
  etykieta: ReactNode;
  pomoc?: string;
  /** Id kontrolki — klik w nazwę ustawia na niej fokus. */
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="ustawienia-wiersz">
      <div className="ustawienia-wiersz-etykieta">
        {htmlFor ? <label htmlFor={htmlFor}>{etykieta}</label> : <span>{etykieta}</span>}
        {pomoc && <Pomoc tekst={pomoc} />}
      </div>
      <div className="ustawienia-wiersz-kontrolka">{children}</div>
    </div>
  );
}

/** Globalny ON/OFF: nazwa (+ „?") z lewej, przełącznik HUD z prawej. */
export function Przelacznik({
  etykieta,
  pomoc,
  checked,
  disabled,
  onChange,
}: {
  etykieta: ReactNode;
  pomoc?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
}) {
  const id = useId();
  return (
    <div className="ustawienia-wiersz przelacznik">
      <div className="ustawienia-wiersz-etykieta">
        <label htmlFor={id}>{etykieta}</label>
        {pomoc && <Pomoc tekst={pomoc} />}
      </div>
      <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />
    </div>
  );
}

/**
 * Składany kafelek jednej sekcji pól (nagłówek + rozwijana treść).
 * Treść rozwija się w pełni (bez wewnętrznego scrolla) — stronę scrolluje się
 * normalnie, zgodnie z życzeniem użytkownika (koniec upakowanej, długiej listy).
 * `znaczek` = krótki status w nagłówku, przed chevronem (Provider AI).
 */
export function KafelekSekcji({
  tytul,
  otwarty,
  onToggle,
  znaczek,
  children,
}: {
  tytul: string;
  otwarty: boolean;
  onToggle: () => void;
  znaczek?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={`ustawienia-pole kafelek-sekcja ${otwarty ? "otwarty" : ""}`}>
      <button
        type="button"
        className="kafelek-sekcja-naglowek"
        onClick={onToggle}
        aria-expanded={otwarty}
      >
        <span className="hud-etykieta">{tytul}</span>
        {znaczek}
        <span className="kafelek-sekcja-chevron" aria-hidden>
          ▾
        </span>
      </button>
      {otwarty && <div className="kafelek-sekcja-tresc">{children}</div>}
    </div>
  );
}

/** Sekcja zakładki Ustawień: nagłówek „01 TYTUŁ ───" (+ opcjonalne „?") + treść. */
export function SekcjaUstawien({
  nr,
  tytul,
  pomoc,
  akcja,
  niebezpieczna = false,
  children,
}: {
  nr: number;
  tytul: string;
  pomoc?: string;
  /** Przycisk przy nagłówku (np. „+ Dodaj"), za linią. */
  akcja?: ReactNode;
  /** Czerwony nagłówek i ramka — akcje nieodwracalne. */
  niebezpieczna?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={`ustawienia-sekcja ${niebezpieczna ? "niebezpieczna" : ""}`}>
      <div className="ustawienia-sekcja-naglowek">
        <span className="ustawienia-sekcja-nr">{String(nr).padStart(2, "0")}</span>
        <h3 className="ustawienia-sekcja-tytul">{tytul}</h3>
        {pomoc && <Pomoc tekst={pomoc} />}
        <span className="ustawienia-sekcja-linia" aria-hidden />
        {akcja}
      </div>
      <div className="ustawienia-sekcja-tresc">{children}</div>
    </section>
  );
}

/**
 * Pole liczby z przyciskami − / + (bez natywnych strzałek). Przycisk na granicy
 * zakresu jest wyszarzony; wpisana wartość zatwierdza się po wyjściu z pola
 * albo Enterze i jest przycinana do [min, max].
 */
export function PoleLiczby({
  wartosc,
  min,
  max,
  krok = 1,
  disabled = false,
  onZmiana,
}: {
  wartosc: number | null;
  min: number;
  max: number;
  krok?: number;
  disabled?: boolean;
  onZmiana: (n: number) => void;
}) {
  const [wpisane, setWpisane] = useState<string | null>(null);
  const przytnij = (n: number) => Math.min(max, Math.max(min, n));
  const zatwierdz = () => {
    if (wpisane === null) return;
    const n = Number(wpisane);
    setWpisane(null);
    if (Number.isFinite(n) && wartosc !== null && przytnij(Math.round(n)) !== wartosc) onZmiana(przytnij(Math.round(n)));
  };
  const zablokowane = disabled || wartosc === null;
  return (
    <span className="pole-liczby">
      <button
        type="button"
        className="btn-maly"
        aria-label="Zmniejsz"
        disabled={zablokowane || wartosc! <= min}
        onClick={() => onZmiana(przytnij(wartosc! - krok))}
      >
        −
      </button>
      <input
        className="ustawienia-select"
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        disabled={zablokowane}
        value={wpisane ?? (wartosc === null ? "" : String(wartosc))}
        onChange={(e) => setWpisane(e.target.value)}
        onBlur={zatwierdz}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
      <button
        type="button"
        className="btn-maly"
        aria-label="Zwiększ"
        disabled={zablokowane || wartosc! >= max}
        onClick={() => onZmiana(przytnij(wartosc! + krok))}
      >
        +
      </button>
    </span>
  );
}
